/**
 * Modelo da cena (mapa da mesa): leitura do documento NDB e as operações que o
 * editor grava. A cena é um item da biblioteca (como no original, onde
 * TfrmSceneModule é um módulo de item da biblioteca); o formato do documento é
 * nosso.
 *
 * Documento:
 * - raiz: grade (`gridSize` em px por célula, `gridVisible`, `gridColor`,
 *   `gridOpacity`), tamanho (`cols` × `rows`), escala (`cellDistance` +
 *   `cellUnit`), fundo (`bgFileId`, um arquivo do RPG Desk, ou `bgUrl`, um endereço
 *   http(s) da web; `bgW` × `bgH` é o tamanho natural, em px; `bgX`, `bgY`, `bgCols` ×
 *   `bgRows` é onde a imagem fica, em células, esticada para preencher, e `bgAngle` o giro
 *   dela em graus, em volta do centro — ver `mapGrid.ts`) e névoa (`fogEnabled`);
 * - visão dos tokens (`docs/spec/scene-walls.md`): `vision`, `ambientLight` (intense | weak |
 *   none) e `fogSharing` (individual | group) na raiz;
 * - filhos `token`: `name`, `x`/`y` (canto superior esquerdo, em células),
 *   `size` (em células), `color`, `fileId`, `layer` (map | tokens | gm),
 *   `hidden`, `showName`, `z`, `hp`/`hpMax`, `facing` (graus, a partir do norte),
 *   `lightOnly` (só emite luz; os jogadores não o veem), a aura (`aura*`, ver `TokenAura` e
 *   `docs/spec/scene-auras.md`), as condições (`cond_<id>`, true; ver
 *   `packages/shared/src/sceneConditions.ts`) e a condição Invisível (`cond_invisible`, a lista de
 *   liberados; ver `packages/shared/src/sceneInvisible.ts`). Filho `sight` com a visão e a luz do
 *   token (só o mestre escreve) e filho `memory` com o que ele já viu;
 * - filhos `fog`: retângulos em células (`x`, `y`, `w`, `h`) com `mode`
 *   reveal | hide | semi | explore, aplicados na ordem.
 * - filho `drawings` (container dos "rabiscos"; o grupo jogadores pode criar nele) com
 *   filhos `drawing`: `kind` free | rect | ellipse | text, `points` ("x,y x,y…", em
 *   px do mapa) ou `x`/`y`/`w`/`h` (px), `text`, `stroke`, `fill` ('' = sem
 *   preenchimento), `width` (px; no texto, o tamanho da letra) e `owner`. Quem
 *   desenha ganha `write` e `delete` no próprio desenho.
 * - filho `walls` com as paredes (`walls.ts`, `docs/spec/scene-walls.md`).
 *
 * Quem controla um token tem `write` nele (permissão NDB por usuário); os mestres
 * passam por cima de tudo.
 */
import { INVISIBLE_ATTR, NDB_GROUPS, conditionAttr, invisibleValue, readConditions, readInvisible, tokenReadPerms, type TokenCondition, type TokenReadState } from '../shared.js';
import { boardFor, roundAngle, roundCells, type BgRect } from './mapGrid.js';
import { readWalls, type Wall } from './walls.js';
import type { AttrValue, NodeTree, Op, TreeNode } from '../ndb.js';

export type TokenLayer = 'map' | 'tokens' | 'gm';
export type FacingMode = 'rotate' | 'arrow';

/**
 * Visão e luz do token, na unidade da cena (metros). Padrões do original (etapa 0): vê 102 sob luz
 * intensa, 1,5 sob luz fraca e nada no escuro; não emite luz. 0 é não ver (ou não iluminar).
 */
export type TokenSight = {
  /** "possui visão": dá visão a NPCs (o token de um jogador sempre dá visão a ele) */
  has: boolean;
  bright: number;
  dim: number;
  dark: number;
  /** ângulo da visão, de 10 a 360 graus */
  angle: number;
  lightBright: number;
  lightDim: number;
  lightAngle: number;
  facingMode: FacingMode;
};
export const SIGHT_DEFAULTS: TokenSight = { has: false, bright: 102, dim: 1.5, dark: 0, angle: 360, lightBright: 0, lightDim: 0, lightAngle: 360, facingMode: 'rotate' };

/**
 * A aura do token (docs/spec/scene-auras.md), como a do plugin de cena do original: uma área
 * translúcida embaixo do token, que anda e gira com ele. Medidas na unidade da cena.
 */
export type AuraShape = 'circle' | 'cone' | 'semicircle' | 'rect' | 'image';
export const AURA_SHAPES: readonly AuraShape[] = ['circle', 'cone', 'semicircle', 'rect', 'image'];
/** os formatos que pintam as células da grade com "Em quadrados" */
export const AURA_GRID_SHAPES: readonly AuraShape[] = ['circle', 'cone', 'semicircle'];

export type TokenAura = {
  shape: AuraShape;
  /** círculo, cone e semicírculo: até onde vai, a partir da borda do token */
  reach: number;
  /** abertura do cone, em graus (10 a 180) */
  angle: number;
  /** retângulo e imagem: largura e altura, centradas no token */
  w: number;
  h: number;
  color: string;
  /** opacidade do preenchimento (da imagem inteira, na imagem) */
  opacity: number;
  /** pinta as células cujo centro cai na forma (círculo, cone e semicírculo) */
  grid: boolean;
  /** imagem: um arquivo do RPG Desk ou um endereço da web */
  fileId: string | null;
  url: string | null;
};

/** opacidade inicial: a forma pintada deixa o mapa aparecer; a imagem, quase inteira (85%, como no original) */
export const AURA_OPACITY = { shape: 0.4, image: 0.85 } as const;
/** A aura que o diálogo propõe: 2 células de alcance, cone de 90° (como no original), vermelha. */
export const newAura = (cellDistance: number): TokenAura => {
  const cell = cellDistance > 0 ? cellDistance : 1;
  return { shape: 'circle', reach: 2 * cell, angle: 90, w: 3 * cell, h: 3 * cell, color: TOKEN_COLORS[0]!, opacity: AURA_OPACITY.shape, grid: false, fileId: null, url: null };
};
/** o maior alcance (e a maior largura ou altura), em células */
export const AURA_MAX_CELLS = 100;
const AURA_ATTRS = ['auraShape', 'auraReach', 'auraAngle', 'auraW', 'auraH', 'auraColor', 'auraOpacity', 'auraGrid', 'auraFileId', 'auraUrl'] as const;

export type SceneToken = {
  id: string;
  name: string;
  x: number;
  y: number;
  size: number;
  color: string;
  fileId: string | null;
  layer: TokenLayer;
  hidden: boolean;
  showName: boolean;
  z: number;
  hp: number | null;
  hpMax: number | null;
  /** usuários com `write` no token */
  controllers: string[];
  /** Invisível (BRAZ-24): quem foi liberado para vê-lo, além dos mestres e de quem o controla (null: visível) */
  invisible: string[] | null;
  /** usuários com entrada de leitura no nó (os do invisível) */
  readers: string[];
  /** as condições ligadas (BRAZ-25), na ordem da lista */
  conditions: TokenCondition[];
  /** direção, em graus no sentido do relógio a partir do norte */
  facing: number;
  /** só emite luz (os jogadores não veem o token) */
  lightOnly: boolean;
  sight: TokenSight;
  /** filho `sight` (null: valem os padrões) */
  sightId: string | null;
  /** a aura (null: sem aura) */
  aura: TokenAura | null;
  /** filho `memory` e os blocos do que o token já viu (`vision.ts`) */
  memoryId: string | null;
  memory: Record<string, string>;
};

export type FogMode = 'reveal' | 'hide' | 'semi' | 'explore';
export type FogRect = { id: string; x: number; y: number; w: number; h: number; mode: FogMode };

export type DrawingKind = 'free' | 'rect' | 'ellipse' | 'text';
export type SceneDrawing = {
  id: string;
  kind: DrawingKind;
  /** traço livre: pares x, y em px do mapa */
  points: number[];
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  stroke: string;
  fill: string | null;
  width: number;
  owner: string | null;
};

export type SceneModel = {
  gridSize: number;
  gridVisible: boolean;
  gridColor: string;
  gridOpacity: number;
  cols: number;
  rows: number;
  cellDistance: number;
  cellUnit: string;
  bgFileId: string | null;
  /** endereço http(s) da imagem do mapa (quando não é um arquivo do RPG Desk) */
  bgUrl: string | null;
  /** tamanho natural da imagem, em px */
  bgW: number;
  bgH: number;
  /** onde a imagem fica no tabuleiro, em células (esticada para preencher) */
  bgX: number;
  bgY: number;
  bgCols: number;
  bgRows: number;
  /** giro da imagem, em graus (sentido do relógio), em volta do centro dela */
  bgAngle: number;
  /** imagem de antes do encaixe (sem `bgCols`): no tamanho natural, a partir do canto */
  bgLegacy: boolean;
  fogEnabled: boolean;
  /** visão dos tokens (com a névoa ligada) */
  vision: boolean;
  ambientLight: 'intense' | 'weak' | 'none';
  fogSharing: 'individual' | 'group';
  tokens: SceneToken[];
  fog: FogRect[];
  /** id do container dos desenhos (null: cena antiga, ainda sem ele) */
  drawingsId: string | null;
  drawings: SceneDrawing[];
  /** id do container das paredes (null: cena sem paredes ainda) */
  wallsId: string | null;
  walls: Wall[];
};

export const SCENE_DEFAULTS = {
  gridSize: 64,
  gridVisible: true,
  gridColor: '#000000',
  gridOpacity: 0.35,
  cols: 24,
  rows: 16,
  cellDistance: 1.5,
  cellUnit: 'm',
} as const;

export const TOKEN_COLORS = ['#e0474c', '#e8833a', '#e3c13b', '#4caf50', '#2fa6a0', '#3d7fe0', '#8a5ad6', '#d65aa8', '#8d8d8d', '#f2f2f2'];

/** As camadas dos tokens, de baixo para cima. */
export const TOKEN_LAYERS: readonly TokenLayer[] = ['map', 'tokens', 'gm'];
const LAYERS = TOKEN_LAYERS;
const FOG_MODES: readonly FogMode[] = ['reveal', 'hide', 'semi', 'explore'];
const AMBIENTS: readonly SceneModel['ambientLight'][] = ['intense', 'weak', 'none'];
export const SIGHT = 'sight';
export const MEMORY = 'memory';

const num = (v: AttrValue | undefined, fallback: number): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : fallback;
};
const str = (v: AttrValue | undefined, fallback: string): string => (typeof v === 'string' && v !== '' ? v : fallback);
const bool = (v: AttrValue | undefined, fallback: boolean): boolean => (typeof v === 'boolean' ? v : v === 'true' ? true : v === 'false' ? false : fallback);
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const FACING_MODES: readonly FacingMode[] = ['rotate', 'arrow'];

function readSight(node: TreeNode | undefined): TokenSight {
  if (!node) return SIGHT_DEFAULTS;
  const a = node.attrs;
  const range = (name: string, fallback: number) => clamp(num(a.get(name), fallback), 0, 100_000);
  const mode = a.get('facingMode');
  return {
    has: bool(a.get('has'), SIGHT_DEFAULTS.has),
    bright: range('bright', SIGHT_DEFAULTS.bright),
    dim: range('dim', SIGHT_DEFAULTS.dim),
    dark: range('dark', SIGHT_DEFAULTS.dark),
    angle: clamp(num(a.get('angle'), SIGHT_DEFAULTS.angle), 10, 360),
    lightBright: range('lightBright', SIGHT_DEFAULTS.lightBright),
    lightDim: range('lightDim', SIGHT_DEFAULTS.lightDim),
    lightAngle: clamp(num(a.get('lightAngle'), SIGHT_DEFAULTS.lightAngle), 10, 360),
    facingMode: FACING_MODES.includes(mode as FacingMode) ? (mode as FacingMode) : SIGHT_DEFAULTS.facingMode,
  };
}

/** A aura gravada no token (sem `auraShape`, ou imagem sem imagem: sem aura), dentro dos limites. */
export function readAura(a: ReadonlyMap<string, AttrValue>, cellDistance: number): TokenAura | null {
  const shape = a.get('auraShape');
  if (!AURA_SHAPES.includes(shape as AuraShape)) return null;
  const base = newAura(cellDistance);
  const cell = cellDistance > 0 ? cellDistance : 1;
  const length = (name: string, fallback: number) => clamp(num(a.get(name), fallback), 0, AURA_MAX_CELLS * cell);
  const fileId = str(a.get('auraFileId'), '') || null;
  const url = fileId ? null : webImageUrl(str(a.get('auraUrl'), ''));
  if (shape === 'image' && !fileId && !url) return null;
  const color = str(a.get('auraColor'), base.color);
  return {
    shape: shape as AuraShape,
    reach: length('auraReach', base.reach),
    angle: clamp(num(a.get('auraAngle'), base.angle), 10, 180),
    w: Math.max(0.05 * cell, length('auraW', base.w)),
    h: Math.max(0.05 * cell, length('auraH', base.h)),
    color: /^#[0-9a-f]{6}$/i.test(color) ? color : base.color,
    opacity: clamp(num(a.get('auraOpacity'), shape === 'image' ? AURA_OPACITY.image : AURA_OPACITY.shape), 0.05, 1),
    grid: AURA_GRID_SHAPES.includes(shape as AuraShape) && bool(a.get('auraGrid'), false),
    fileId,
    url,
  };
}

/** Os atributos da aura, só os que o formato usa (null apaga; sem aura, apaga todos). */
export function auraAttrs(aura: TokenAura | null): Record<(typeof AURA_ATTRS)[number], AttrValue | null> {
  const r2 = (v: number) => Math.round(v * 100) / 100;
  const sized = aura?.shape === 'rect' || aura?.shape === 'image';
  return {
    auraShape: aura?.shape ?? null,
    auraReach: aura && !sized ? r2(aura.reach) : null,
    auraAngle: aura?.shape === 'cone' ? Math.round(aura.angle) : null,
    auraW: aura && sized ? r2(aura.w) : null,
    auraH: aura && sized ? r2(aura.h) : null,
    auraColor: aura && aura.shape !== 'image' ? aura.color : null,
    auraOpacity: aura ? r2(aura.opacity) : null,
    auraGrid: aura?.grid && AURA_GRID_SHAPES.includes(aura.shape) ? true : null,
    auraFileId: aura?.shape === 'image' ? aura.fileId : null,
    auraUrl: aura?.shape === 'image' && !aura.fileId ? aura.url : null,
  };
}

/** Grava a aura do token (null tira); só o que mudou. Quem controla o token pode (é o nó dele). */
export function auraOps(token: Pick<SceneToken, 'id' | 'aura'>, aura: TokenAura | null): Op[] {
  const before = auraAttrs(token.aura);
  const after = auraAttrs(aura);
  return setOps(token.id, Object.fromEntries(Object.entries(after).filter(([k, v]) => v !== before[k as keyof typeof before])));
}

function readToken(node: TreeNode, index: number, cellDistance: number): SceneToken {
  const a = node.attrs;
  const layer = a.get('layer');
  const hp = a.get('hp');
  const hpMax = a.get('hpMax');
  const sight = node.children.find((c) => c.name === SIGHT);
  const memory = node.children.find((c) => c.name === MEMORY);
  return {
    id: node.id,
    name: str(a.get('name'), ''),
    x: num(a.get('x'), 0),
    y: num(a.get('y'), 0),
    size: clamp(num(a.get('size'), 1), 0.25, 20),
    color: str(a.get('color'), TOKEN_COLORS[0]!),
    fileId: str(a.get('fileId'), '') || null,
    layer: LAYERS.includes(layer as TokenLayer) ? (layer as TokenLayer) : 'tokens',
    hidden: bool(a.get('hidden'), false),
    showName: bool(a.get('showName'), true),
    z: num(a.get('z'), index),
    hp: hp === undefined || hp === '' ? null : num(hp, 0),
    hpMax: hpMax === undefined || hpMax === '' ? null : num(hpMax, 0),
    controllers: node.perms.filter((p) => p.kind === 'user' && p.permission === 'write' && p.allowance !== 'deny').map((p) => p.id),
    invisible: readInvisible(a.get(INVISIBLE_ATTR)),
    readers: node.perms.filter((p) => p.kind === 'user' && p.permission === 'read').map((p) => p.id),
    conditions: readConditions((name) => a.get(name)),
    facing: ((num(a.get('facing'), 0) % 360) + 360) % 360,
    lightOnly: bool(a.get('lightOnly'), false),
    sight: readSight(sight),
    sightId: sight?.id ?? null,
    aura: readAura(a, cellDistance),
    memoryId: memory?.id ?? null,
    memory: memory ? Object.fromEntries([...memory.attrs].filter((e): e is [string, string] => typeof e[1] === 'string')) : {},
  };
}

const DRAWING_KINDS: readonly DrawingKind[] = ['free', 'rect', 'ellipse', 'text'];

function readDrawing(node: TreeNode): SceneDrawing {
  const a = node.attrs;
  const kind = a.get('kind');
  const points = str(a.get('points'), '')
    .split(/[\s,]+/)
    .map(Number)
    .filter((n) => Number.isFinite(n));
  return {
    id: node.id,
    kind: DRAWING_KINDS.includes(kind as DrawingKind) ? (kind as DrawingKind) : 'free',
    points: points.length % 2 ? points.slice(0, -1) : points,
    x: num(a.get('x'), 0),
    y: num(a.get('y'), 0),
    w: num(a.get('w'), 0),
    h: num(a.get('h'), 0),
    text: str(a.get('text'), ''),
    stroke: str(a.get('stroke'), '#e0474c'),
    fill: str(a.get('fill'), '') || null,
    width: clamp(num(a.get('width'), 4), 0.5, 200),
    owner: str(a.get('owner'), '') || null,
  };
}

export function readScene(tree: NodeTree): SceneModel {
  const a = tree.root.attrs;
  const gridSize = clamp(num(a.get('gridSize'), SCENE_DEFAULTS.gridSize), 16, 512);
  const bgW = num(a.get('bgW'), 0);
  const bgH = num(a.get('bgH'), 0);
  // cena de antes do encaixe: a imagem no tamanho natural, a partir do canto
  const bgCols = num(a.get('bgCols'), 0) > 0 ? num(a.get('bgCols'), 0) : bgW / gridSize;
  const bgRows = num(a.get('bgRows'), 0) > 0 ? num(a.get('bgRows'), 0) : bgH / gridSize;
  const tokens: SceneToken[] = [];
  const fog: FogRect[] = [];
  const container = tree.findChild(tree.root.id, DRAWINGS);
  const drawings = (container?.children ?? []).filter((c) => c.name === 'drawing').map(readDrawing);
  const cellDistance = num(a.get('cellDistance'), SCENE_DEFAULTS.cellDistance);
  tree.root.children.forEach((child, i) => {
    if (child.name === 'token') tokens.push(readToken(child, i, cellDistance));
    else if (child.name === 'fog') {
      const c = child.attrs;
      const mode = c.get('mode');
      fog.push({ id: child.id, x: num(c.get('x'), 0), y: num(c.get('y'), 0), w: num(c.get('w'), 0), h: num(c.get('h'), 0), mode: FOG_MODES.includes(mode as FogMode) ? (mode as FogMode) : 'reveal' });
    }
  });
  // camadas: mapa embaixo, depois tokens, depois os do mestre; dentro da camada, pelo z
  tokens.sort((p, q) => LAYERS.indexOf(p.layer) - LAYERS.indexOf(q.layer) || p.z - q.z);
  return {
    gridSize,
    gridVisible: bool(a.get('gridVisible'), SCENE_DEFAULTS.gridVisible),
    gridColor: str(a.get('gridColor'), SCENE_DEFAULTS.gridColor),
    gridOpacity: clamp(num(a.get('gridOpacity'), SCENE_DEFAULTS.gridOpacity), 0, 1),
    cols: clamp(Math.round(num(a.get('cols'), SCENE_DEFAULTS.cols)), 1, 400),
    rows: clamp(Math.round(num(a.get('rows'), SCENE_DEFAULTS.rows)), 1, 400),
    cellDistance,
    cellUnit: str(a.get('cellUnit'), SCENE_DEFAULTS.cellUnit),
    bgFileId: str(a.get('bgFileId'), '') || null,
    bgUrl: webImageUrl(str(a.get('bgUrl'), '')),
    bgW,
    bgH,
    bgX: num(a.get('bgX'), 0),
    bgY: num(a.get('bgY'), 0),
    bgCols,
    bgRows,
    bgAngle: clamp(num(a.get('bgAngle'), 0), -45, 45),
    bgLegacy: !(num(a.get('bgCols'), 0) > 0) && bgW > 0 && !!(str(a.get('bgFileId'), '') || webImageUrl(str(a.get('bgUrl'), ''))),
    fogEnabled: bool(a.get('fogEnabled'), false),
    vision: bool(a.get('vision'), false),
    ambientLight: AMBIENTS.includes(a.get('ambientLight') as never) ? (a.get('ambientLight') as SceneModel['ambientLight']) : 'intense',
    fogSharing: a.get('fogSharing') === 'group' ? 'group' : 'individual',
    tokens,
    fog,
    drawingsId: container?.id ?? null,
    drawings,
    ...readWalls(tree),
  };
}

/* ------------------------------------------------------------------ desenhos */

export const DRAWINGS = 'drawings';

/** Container dos desenhos para cenas antigas (o mestre cria; os jogadores podem desenhar nele). */
export function drawingsContainerOps(id: string, rootId: string): Op[] {
  return [
    { t: 'add', parent: rootId, id, name: DRAWINGS },
    { t: 'perm', node: id, kind: 'group', id: NDB_GROUPS.players, permission: 'createChild', allowance: 'allow' },
  ];
}

export type NewDrawing = Omit<SceneDrawing, 'id' | 'owner'>;

/** Um desenho novo; quem desenhou pode mudá-lo e apagá-lo. */
export function addDrawingOps(containerId: string, id: string, d: NewDrawing, owner: string | null): Op[] {
  const r1 = (v: number) => Math.round(v * 10) / 10;
  const attrs: Record<string, AttrValue | null> = {
    kind: d.kind,
    stroke: d.stroke,
    fill: d.fill ?? '',
    width: r1(d.width),
    owner: owner ?? '',
  };
  if (d.kind === 'free') attrs.points = d.points.map(r1).join(' ');
  else Object.assign(attrs, { x: r1(d.x), y: r1(d.y), w: r1(d.w), h: r1(d.h) });
  if (d.kind === 'text') attrs.text = d.text;
  return [
    { t: 'add', parent: containerId, id, name: 'drawing' },
    ...setOps(id, attrs),
    ...(owner ? (['write', 'delete'] as const).map((permission): Op => ({ t: 'perm', node: id, kind: 'user', id: owner, permission, allowance: 'allow' })) : []),
  ];
}

export const canEditDrawing = (d: SceneDrawing, userId: string | undefined, isGm: boolean) => isGm || (!!userId && d.owner === userId);

/** O que a pessoa vê: jogadores não veem tokens ocultos nem a camada do mestre. */
export function visibleTokens(scene: SceneModel, isGm: boolean): SceneToken[] {
  return isGm ? scene.tokens : scene.tokens.filter((t) => !t.hidden && t.layer !== 'gm');
}

export function canMoveToken(token: SceneToken, userId: string | undefined, isGm: boolean): boolean {
  return isGm || (!!userId && token.controllers.includes(userId));
}

/* ------------------------------------------------------------------ operações */

export type NewToken = Partial<Omit<SceneToken, 'id' | 'controllers' | 'invisible' | 'readers' | 'conditions'>> & { name: string };

export function addTokenOps(id: string, token: NewToken, scene: SceneModel): Op[] {
  const z = scene.tokens.reduce((max, t) => Math.max(max, t.z + 1), 0);
  const ops: Op[] = [{ t: 'add', parent: 'root', id, name: 'token' }];
  const attrs: Record<string, AttrValue> = {
    name: token.name,
    x: token.x ?? 0,
    y: token.y ?? 0,
    size: token.size ?? 1,
    color: token.color ?? TOKEN_COLORS[scene.tokens.length % TOKEN_COLORS.length]!,
    layer: token.layer ?? 'tokens',
    z,
  };
  if (token.fileId) attrs.fileId = token.fileId;
  for (const [name, value] of Object.entries(attrs)) ops.push({ t: 'set', node: id, name, value });
  return ops;
}

/**
 * Cópia de um token (outro id), deslocada e na frente: tudo o que ele tem (valores, camada, oculto,
 * direção, só luz, condições, o Invisível com os liberados, quem controla, a visão, a luz e a aura),
 * com as mesmas permissões. Fica de fora o que é do token no mapa: a memória do que ele viu. Grava o
 * mestre (o Invisível e as permissões de leitura só ele grava direto).
 */
export function duplicateTokenOps(id: string, token: SceneToken, scene: SceneModel, dx: number, dy: number, newId: () => string): Op[] {
  const ops = addTokenOps(id, { name: token.name, x: token.x + dx, y: token.y + dy, size: token.size, color: token.color, layer: token.layer, ...(token.fileId ? { fileId: token.fileId } : {}) }, scene);
  ops.push(...setOps(id, { hidden: token.hidden, showName: token.showName, hp: token.hp ?? null, hpMax: token.hpMax ?? null }));
  const attrs: Record<string, AttrValue | null> = { ...auraAttrs(token.aura), facing: token.facing || null, lightOnly: token.lightOnly || null, [INVISIBLE_ATTR]: invisibleValue(token.invisible) };
  for (const c of token.conditions) attrs[conditionAttr(c)] = true;
  ops.push(...setOps(id, Object.fromEntries(Object.entries(attrs).filter(([, v]) => v !== null))));
  for (const user of token.controllers) ops.push(...setControllerOps(id, user, true));
  for (const p of tokenReadPerms(token, [])) if (p.allowance) ops.push({ t: 'perm', node: id, kind: p.kind, id: p.id, permission: 'read', allowance: p.allowance });
  ops.push(...newSightOps(id, token.sight, newId));
  return ops;
}

/** Só endereços http(s) valem como imagem da web; o resto vira null. */
export function webImageUrl(value: string): string | null {
  const url = value.trim();
  return /^https?:\/\/[^\s]+$/i.test(url) && url.length <= 2000 ? url : null;
}

/**
 * Fundo novo: um arquivo do RPG Desk (`fileId`) ou um endereço da web (`url`), com o tamanho
 * natural da imagem em px e onde ela fica, em células (`rectFromImageGrid` ou
 * `rectByRounding`). O tabuleiro passa a ser o que a imagem cobre.
 */
export function backgroundOps(source: { fileId: string } | { url: string }, size: { width: number; height: number }, rect: BgRect): Op[] {
  return setOps('root', {
    bgFileId: 'fileId' in source ? source.fileId : null,
    bgUrl: 'url' in source ? source.url : null,
    bgW: size.width,
    bgH: size.height,
    ...placementAttrs(rect),
    ...boardFor(rect),
  });
}

const placementAttrs = (rect: BgRect) => ({
  bgX: roundCells(rect.x),
  bgY: roundCells(rect.y),
  bgCols: roundCells(rect.w),
  bgRows: roundCells(rect.h),
  // reto não grava nada (cenas sem giro ficam como eram)
  bgAngle: rect.angle ? roundAngle(clamp(rect.angle, -45, 45)) : null,
});

/** Move ou estica a imagem (o tabuleiro e o que está nele ficam onde estão). */
export const placementOps = (rect: BgRect): Op[] => setOps('root', placementAttrs({ ...rect, w: Math.max(0.05, rect.w), h: Math.max(0.05, rect.h) }));

/** A imagem como está na cena, em células (null: sem imagem). */
export const sceneBgRect = (scene: SceneModel): BgRect | null =>
  (scene.bgFileId || scene.bgUrl) && scene.bgCols > 0 && scene.bgRows > 0
    ? { x: scene.bgX, y: scene.bgY, w: scene.bgCols, h: scene.bgRows, ...(scene.bgAngle ? { angle: scene.bgAngle } : {}) }
    : null;

/** Tira a imagem do mapa (arquivo ou endereço). */
export const clearBackgroundOps = (): Op[] => setOps('root', { bgFileId: null, bgUrl: null, bgW: null, bgH: null, bgX: null, bgY: null, bgCols: null, bgRows: null, bgAngle: null });

export function setOps(node: string, attrs: Record<string, AttrValue | null>): Op[] {
  return Object.entries(attrs).map(([name, value]) => ({ t: 'set', node, name, value }));
}

/**
 * As permissões de leitura do token depois de mudar o que pesa nelas (oculto, camada, quem controla):
 * o Ocultar e a camada do mestre escondem de todos, e o Invisível (BRAZ-24) deixa só quem controla e
 * os liberados. Quem grava é o mestre; o invisível em si muda pelo servidor (`scene.invisible`).
 */
export function readPermOps(token: SceneToken, next: Partial<Pick<TokenReadState, 'hidden' | 'layer' | 'controllers'>> = {}): Op[] {
  const state: TokenReadState = {
    hidden: next.hidden ?? token.hidden,
    layer: next.layer ?? token.layer,
    invisible: token.invisible,
    controllers: next.controllers ?? token.controllers,
  };
  return tokenReadPerms(state, token.readers).map((p): Op => ({ t: 'perm', node: token.id, kind: p.kind, id: p.id, permission: 'read', allowance: p.allowance }));
}

/** Liga ou desliga uma condição do token (BRAZ-25). Quem controla o token pode (é o nó dele). */
export const conditionOps = (tokenId: string, id: TokenCondition, on: boolean): Op[] => setOps(tokenId, { [conditionAttr(id)]: on ? true : null });

export function setControllerOps(tokenId: string, userId: string, on: boolean): Op[] {
  return [{ t: 'perm', node: tokenId, kind: 'user', id: userId, permission: 'write', allowance: on ? 'allow' : null }];
}

/** Traz para a frente (ou manda para trás) dentro da camada. */
export function restackOps(scene: SceneModel, tokenId: string, where: 'front' | 'back'): Op[] {
  const zs = scene.tokens.map((t) => t.z);
  const z = where === 'front' ? Math.max(...zs) + 1 : Math.min(...zs) - 1;
  return [{ t: 'set', node: tokenId, name: 'z', value: z }];
}

/**
 * Grava a visão e a luz do token. O filho `sight` nasce com `write` negado ao grupo jogadores:
 * quem controla o token o move, mas não aumenta a própria visão (o mestre passa por cima).
 */
export function sightOps(token: Pick<SceneToken, 'id' | 'sight' | 'sightId'>, sight: TokenSight, newId: () => string): Op[] {
  const changed = Object.fromEntries(
    (Object.keys(SIGHT_DEFAULTS) as (keyof TokenSight)[])
      .filter((k) => sight[k] !== token.sight[k])
      .map((k) => [k, sight[k] === SIGHT_DEFAULTS[k] ? null : sight[k]] as const),
  );
  if (!Object.keys(changed).length) return [];
  let id = token.sightId;
  const ops: Op[] = [];
  if (!id) {
    id = newId();
    ops.push({ t: 'add', parent: token.id, id, name: SIGHT }, { t: 'perm', node: id, kind: 'group', id: NDB_GROUPS.players, permission: 'write', allowance: 'deny' });
  }
  ops.push(...setOps(id, changed).filter((op) => token.sightId || op.t !== 'set' || op.value !== null));
  return ops;
}

/** Visão e luz de um token novo (ainda sem o filho `sight`). */
export const newSightOps = (tokenId: string, sight: TokenSight, newId: () => string): Op[] => sightOps({ id: tokenId, sight: SIGHT_DEFAULTS, sightId: null }, sight, newId);

/**
 * Filho `memory` do token: o mestre cria (quem controla o token não cria filhos nele); quem controla
 * o token escreve nele, porque o filho herda o `write` do token.
 */
export const memoryContainerOps = (token: SceneToken, id: string): Op[] => [{ t: 'add', parent: token.id, id, name: MEMORY }];

/** Blocos novos da memória do token (`vision.ts`). */
export const memoryOps = (token: SceneToken, tiles: Record<string, string>): Op[] => (token.memoryId ? setOps(token.memoryId, tiles) : []);

/** "Reiniciar a exploração": apaga a memória de todos os tokens. */
export const resetMemoryOps = (scene: SceneModel): Op[] => scene.tokens.filter((t) => t.memoryId).map((t): Op => ({ t: 'clear', node: t.memoryId! }));

export function addFogOps(id: string, rect: Omit<FogRect, 'id'>): Op[] {
  return [{ t: 'add', parent: 'root', id, name: 'fog' }, ...setOps(id, { x: rect.x, y: rect.y, w: rect.w, h: rect.h, mode: rect.mode })];
}

export function clearFogOps(): Op[] {
  return [{ t: 'rmChildren', node: 'root', name: 'fog' }];
}

/* ------------------------------------------------------------------ geometria */

/** Encaixa na grade: tokens de tamanho inteiro no canto da célula; menores, na meia célula. */
export function snap(value: number, size: number): number {
  const step = size >= 1 ? 1 : 0.5;
  return Math.round(value / step) * step;
}

/**
 * Onde o token solto fica: com ao menos uma célula dele dentro do mapa (ou ele inteiro, se for menor
 * que uma). Os grandes podem passar da borda; o de meia célula chega às meias-células da borda, que a
 * conta de antes (de 1 - tamanho até a última célula) deixava de fora dos dois lados.
 */
export function clampToken(x: number, y: number, size: number, cols: number, rows: number): { x: number; y: number } {
  const inside = Math.min(size, 1);
  const lo = inside - size;
  const fit = (v: number, n: number) => Math.min(Math.max(v, lo), Math.max(lo, n - inside));
  return { x: fit(x, cols), y: fit(y, rows) };
}

/** Distância em células à moda dos jogos de grade (diagonal conta 1). */
export function gridDistance(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(bx - ax), Math.abs(by - ay));
}

/** Normaliza um retângulo arrastado (em células) para x/y/w/h positivos e encaixados. */
export function cellRect(ax: number, ay: number, bx: number, by: number): { x: number; y: number; w: number; h: number } {
  const x = Math.floor(Math.min(ax, bx));
  const y = Math.floor(Math.min(ay, by));
  const x2 = Math.ceil(Math.max(ax, bx));
  const y2 = Math.ceil(Math.max(ay, by));
  return { x, y, w: Math.max(1, x2 - x), h: Math.max(1, y2 - y) };
}
